import * as moment from 'moment'
import * as React from 'react'
import { isTracepointActive, tracepointStartAt, TracepointRule } from './domain/TracepointRule'
import Countdown from './helper/countdown'
import Link from './helper/link'
import MomentDate from './momentDate'

interface OwnProps {
    tracepoint: TracepointRule<moment.Moment>
    index?: number
    showTraceLinks?: boolean
}

interface ReduxStateProps {}

interface DispatchProps {}

interface State {}

type Props = OwnProps & ReduxStateProps & DispatchProps

const callgraphDescription = (tracepoint: TracepointRule<any>): string => {
    switch (tracepoint.callgraphDuration) {
        case 1:
            return 'for the first 15 minutes'
        case 2:
            return 'for the entire boost duration'
    }

    return 'until the first Callgraphs are collected'
}

const callgraphsPossible = (tracepoint: TracepointRule<any>): boolean =>
    tracepoint.report !== undefined && tracepoint.report.traceCollectionReport.supportsCallgraphStats && tracepoint.enableCallgraph

const tracepointHasConditions = (tracepoint: TracepointRule<any>): boolean =>
    (tracepoint.filterThresholdMemoryMb && tracepoint.filterThresholdMemoryMb > 0) ||
    (tracepoint.filterThresholdMs && tracepoint.filterThresholdMs > 0) ||
    tracepoint.annotations.length > 0

const notifyLabelClasses = (done: boolean): string => (done ? 'tw-ml-8 label label-outline-success' : 'tw-ml-8 label label-outline-default')

export class TracepointListItem extends React.PureComponent<Props, State> {
    public render() {
        const { tracepoint, index = 0, showTraceLinks = true } = this.props
        const cellPadding = 'tw-pt-8 tw-pb-8'
        const now = moment.utc()
        const startAt = tracepointStartAt(tracepoint)
        const active = isTracepointActive(tracepoint, now)
        const scheduled = !tracepoint.completed && startAt.isAfter(now)

        return (
            <tr key={tracepoint.id} className={index % 2 === 0 ? 'odd' : 'even'} style={active ? { borderLeft: '2px solid orange' } : {}}>
                <td colSpan={6} className={cellPadding + ' tw-pl-16'}>
                    <h4 className="tw-font-size-16" style={{ marginBottom: 0, marginTop: 0 }}>
                        {tracepoint.generatedBy == 'automatic' ? (
                            <span className="label label-info" style={{ marginRight: 5, marginLeft: 5 }}>
                                Automatic
                            </span>
                        ) : null}
                        {tracepoint.generatedBy === 'performance_report' ? (
                            <span className="label label-info" style={{ marginRight: 5, marginLeft: 5 }}>
                                Performance Report
                            </span>
                        ) : null}
                        <Link
                            route="XhprofProfilerBundle.Profile.tracepoint"
                            params={{ tracepointId: tracepoint.id }}
                            query={{ cid: tracepoint.referenceId, sortBy: 'wt' }}
                            className="tw-pr-8"
                        >
                            <span className="tw-font-weight-normal">Transaction</span> {tracepoint.transaction}
                            {tracepoint.transactionTags.map(tag => (
                                <span key={tag} className="label label-outline-default tw-ml-8">
                                    {tag}
                                </span>
                            ))}
                        </Link>

                        {active && (
                            <Countdown countdownEnd={tracepoint.activeUntil} className="label label-outline-burning">
                                <i className="fa fa-clock-o" />
                            </Countdown>
                        )}
                    </h4>
                    <div className="tw-pl-8">
                        <span className="tw-font-weight-strong">
                            <span className="text-mute tw-font-weight-normal">service</span> {tracepoint.service}
                            <span className="text-mute tw-font-weight-normal"> environment</span> {tracepoint.environment}
                        </span>
                        &nbsp;
                        <span className="tw-font-weight-strong">
                            <span className="tw-font-weight-normal">{scheduled ? 'scheduled ' : 'active '}</span>
                            <MomentDate date={startAt} />
                            <span className="tw-font-weight-normal tw-ml-4 tw-mr-4">for</span>
                            {this.getDurationLabel(tracepoint.duration)}
                        </span>
                        {tracepoint.enableCallgraph && (
                            <p className="tw-mb-0">
                                <span className="text-mute">Callgraph</span> <strong>{callgraphDescription(tracepoint)}</strong>
                            </p>
                        )}
                        {tracepointHasConditions(tracepoint) ? (
                            <p className="tw-mb-0">
                                <span className="text-mute">Filter</span>
                                {tracepoint.filterThresholdMemoryMb ? (
                                    <span className="tw-ml-4">
                                        <span className="text-mute">&bull;</span> &ge;
                                        {tracepoint.filterThresholdMemoryMb}
                                        MB
                                    </span>
                                ) : null}
                                {tracepoint.filterThresholdMs ? (
                                    <span className="tw-ml-4">
                                        <span className="text-mute">&bull;</span> &ge;{tracepoint.filterThresholdMs}
                                        ms
                                    </span>
                                ) : null}
                                {tracepoint.annotations.map(annotation => (
                                    <span className="tw-ml-4" key={annotation.name}>
                                        <span className="text-mute">&bull;</span>
                                        {` ${annotation.name} "${annotation.value}"`}
                                    </span>
                                ))}
                            </p>
                        ) : null}
                        {!tracepoint.traceReference ? null : (
                            <p className="tw-mb-0">
                                <Link
                                    className="tw-color-grey-500"
                                    route="XhprofProfilerBundle.Profile.quick"
                                    params={{
                                        profile: tracepoint.traceReference.aggregationId,
                                        s: tracepoint.service,
                                        env: tracepoint.environment,
                                        measured: startAt.unix(),
                                    }}
                                >
                                    <i
                                        className="tw-ml-4 fa fa-tasks tw-color-grey-600"
                                        title="Aggregated Trace: averaged representation of multiple Traces"
                                    />{' '}
                                    <span className="text-mute">Aggregated Trace</span>{' '}
                                    {Math.round(tracepoint.traceReference.aggregationResponseTime / 1000)}ms
                                </Link>
                            </p>
                        )}
                        {tracepoint.completed ? null : (
                            <p className="tw-m-0 tw-line-height-40">
                                {!tracepoint.notifyOnFirstSlow && !tracepoint.notifyOnFirst && !tracepoint.notifyOnCompletion ? (
                                    <span>No notifications</span>
                                ) : (
                                    <>
                                        Notify on
                                        {tracepoint.notifyOnCompletion ? (
                                            <span className={notifyLabelClasses(tracepoint.notificationSentOnCompletion)}>
                                                {tracepoint.notificationSentOnCompletion && <i className="fa fa-check" />}
                                                completion
                                            </span>
                                        ) : null}
                                        {tracepoint.notifyOnFirst ? (
                                            <span className={notifyLabelClasses(tracepoint.notificationSentOnFirst)}>
                                                {tracepoint.notificationSentOnFirst && <i className="fa fa-check" />} first trace
                                            </span>
                                        ) : null}
                                        {tracepoint.notifyOnFirstSlow ? (
                                            <span className={notifyLabelClasses(tracepoint.notificationSentOnFirstSlow)}>
                                                {tracepoint.notificationSentOnFirstSlow && <i className="fa fa-check" />}
                                                first trace &ge; {tracepoint.notifyOnFirstSlowThreshold}ms
                                            </span>
                                        ) : null}
                                    </>
                                )}
                            </p>
                        )}
                    </div>
                </td>
                <td colSpan={5} className={cellPadding + ' number'}>
                    {!tracepoint.report || !showTraceLinks ? null : (
                        <React.Fragment>
                            {tracepoint.report.traceCollectionReport.tracesSlice.length > 0 && (
                                <ul className="list-inline">
                                    {tracepoint.report.traceCollectionReport.tracesSlice.map(trace => (
                                        <li key={trace.id}>
                                            <Link
                                                className="tw-color-grey-500"
                                                route="XhprofProfilerBundle.Profile.quick"
                                                params={{
                                                    profile: trace.id,
                                                    s: tracepoint.service,
                                                    env: tracepoint.environment,
                                                    measured: startAt.unix(),
                                                }}
                                            >
                                                {' '}
                                                {Math.round(trace.wallTime / 1000)}ms
                                            </Link>
                                        </li>
                                    ))}
                                    <li>
                                        <Link
                                            route="XhprofProfilerBundle.Profile.list"
                                            className="label label-outline-default tw-font-size-12"
                                            params={{
                                                cid: tracepoint.referenceId,
                                                env: tracepoint.environment,
                                                s: tracepoint.service,
                                                sortBy: 'wt',
                                            }}
                                        >
                                            show all traces...
                                        </Link>
                                    </li>
                                </ul>
                            )}
                            {!callgraphsPossible(tracepoint)
                                ? null
                                : tracepoint.report.traceCollectionReport.callgraphsSlice.length > 0 && (
                                      <ul className="list-inline">
                                          {tracepoint.report.traceCollectionReport.callgraphsSlice.map(trace => (
                                              <li key={trace.id}>
                                                  <Link
                                                      className="tw-color-grey-500"
                                                      route="XhprofProfilerBundle.Profile.quick"
                                                      params={{
                                                          profile: trace.id,
                                                          s: tracepoint.service,
                                                          env: tracepoint.environment,
                                                          measured: startAt.unix(),
                                                      }}
                                                  >
                                                      {' '}
                                                      {Math.round(trace.wallTime / 1000)}ms
                                                  </Link>
                                              </li>
                                          ))}
                                          <li>
                                              <Link
                                                  route="XhprofProfilerBundle.Profile.list"
                                                  className="label label-outline-default tw-font-size-12"
                                                  params={{
                                                      cid: tracepoint.referenceId,
                                                      env: tracepoint.environment,
                                                      s: tracepoint.service,
                                                      sortBy: 'wt',
                                                      parseType: 1,
                                                  }}
                                              >
                                                  show all callgraphs .. <i className="fa fa-sitemap" title="callgraph traces" />
                                              </Link>
                                          </li>
                                      </ul>
                                  )}
                        </React.Fragment>
                    )}
                </td>
            </tr>
        )
    }

    private getDurationLabel(duration: number): string {
        if (duration < 60) {
            return `${duration} minutes`
        }

        if (duration === 60) {
            return '1 hour'
        }

        return `${Math.round(duration / 60)} hours`
    }
}
